import { User, UsersListResponse, Department, DepartmentsListResponse } from '../types';
import apiClient from './apiClient';

export interface CreateUserData {
  email: string;
  name: string;
  employee_code: string;
  department_id: number;
  job_position?: string;
  category?: string;
  manager_id?: number;
  roles?: string[];
  is_active?: boolean;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  employee_code?: string;
  job_position?: string;
  category?: string;
  department_id?: number;
  manager_id?: number;
  roles?: string[];
  is_active?: boolean;
}

export interface CreateUserResponse {
  message: string;
  user: User;
  temporary_password: string;
}

export interface UserGroupNode {
  label: string;
  field: string;
  field_value: string;
  key: string;
  count: number;
  sub_groups?: UserGroupNode[];
}

export interface GroupedUsersParams {
  group_by: string;
  is_active?: string;
  department_id?: number;
  manager_id?: number;
  role?: string;
  category?: string;
  practice?: string;
  job_position?: string;
  search?: string;
  // Column-specific searches
  search_name?: string;
  search_email?: string;
  search_employee_code?: string;
  search_department?: string;
  search_manager?: string;
  search_role?: string;
  [key: string]: string | number | undefined;
}

/**
 * Users API service
 */
export const usersApi = {
  /**
   * Get all users with optional filters
   */
  getUsers: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    is_active?: string;
    department_id?: number;
    role?: string;
    manager_id?: number;
    category?: string;
    practice?: string;
    job_position?: string;
    // Column-specific searches
    search_name?: string;
    search_email?: string;
    search_employee_code?: string;
    search_department?: string;
    search_manager?: string;
    search_role?: string;
    [key: string]: string | number | undefined;
  }): Promise<UsersListResponse> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/users?${queryString}` : '/users';
    return apiClient.get<UsersListResponse>(endpoint);
  },

  /**
   * Get users grouped by a field
   */
  getUsersGrouped: async (params: GroupedUsersParams): Promise<{ groups: UserGroupNode[]; total: number; group_by: string[] }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    return apiClient.get(`/users/grouped?${queryParams.toString()}`);
  },

  /**
   * Export users to CSV or XLSX
   */
  exportUsers: async (params?: {
    format?: 'csv' | 'xlsx';
    search?: string;
    is_active?: string;
    department_id?: number;
    role?: string;
    manager_id?: number;
    columns?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params?.format) queryParams.append('format', params.format);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.is_active) queryParams.append('is_active', params.is_active);
    if (params?.department_id) queryParams.append('department_id', params.department_id.toString());
    if (params?.role) queryParams.append('role', params.role);
    if (params?.manager_id) queryParams.append('manager_id', params.manager_id.toString());
    if (params?.columns) queryParams.append('columns', params.columns);
    
    const queryString = queryParams.toString();
    const endpoint = `/users/export${queryString ? `?${queryString}` : ''}`;
    return apiClient.getBlob(endpoint);
  },

  /**
   * Get user by ID
   */
  getUser: async (userId: number): Promise<{ user: User }> => {
    return apiClient.get<{ user: User }>(`/users/${userId}`);
  },

  /**
   * Get current user
   */
  getMe: async (): Promise<{ user: User }> => {
    return apiClient.get<{ user: User }>('/users/me');
  },

  /**
   * Create a new user
   */
  createUser: async (data: CreateUserData): Promise<CreateUserResponse> => {
    return apiClient.post<CreateUserResponse>('/users', data);
  },

  /**
   * Update user
   */
  updateUser: async (userId: number, data: UpdateUserData): Promise<{ message: string; user: User }> => {
    return apiClient.put<{ message: string; user: User }>(`/users/${userId}`, data);
  },

  /**
   * Deactivate user (soft delete)
   */
  deactivateUser: async (userId: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/users/${userId}`);
  },

  /**
   * Reset user password
   */
  resetPassword: async (userId: number): Promise<{ message: string; temporary_password: string }> => {
    return apiClient.post<{ message: string; temporary_password: string }>(`/users/${userId}/reset-password`);
  },

  /**
   * Get employees without any active project assignment
   */
  getUnassignedEmployees: async (params?: {
    search?: string;
    department_id?: number;
  }): Promise<{ employees: User[]; total: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.department_id) queryParams.append('department_id', params.department_id.toString());
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/users/unassigned?${queryString}` : '/users/unassigned';
    return apiClient.get<{ employees: User[]; total: number }>(endpoint);
  },

  /**
   * Get all employees for assignment (with optional filter to exclude already assigned to a project)
   */
  getEmployeesForAssignment: async (params?: {
    search?: string;
    exclude_project_id?: number;
  }): Promise<{ employees: Array<{ id: number; name: string; email: string; employee_code: string }>; total: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.exclude_project_id) queryParams.append('exclude_project_id', params.exclude_project_id.toString());
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/users/employees?${queryString}` : '/users/employees';
    return apiClient.get(endpoint);
  },

  /**
   * Get users who can be managers (for manager dropdown)
   * Only returns users with 'manager' role
   */
  getManagers: async (params?: {
    search?: string;
  }): Promise<{ managers: Array<{ id: number; name: string; email: string; employee_code: string; department: string | null }>; total: number }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/users/managers?${queryString}` : '/users/managers';
    return apiClient.get(endpoint);
  },

  getSalesPersons: async (): Promise<{ sales_persons: Array<{ id: number; name: string; email: string; department: string | null }>; total: number }> => {
    return apiClient.get('/users/sales-persons');
  },
};

export default usersApi;
